import React, { LegacyRef, useCallback, useEffect } from 'react';

import { Menu, MenuButton, MenuItem, MenuList, rem } from '@vimeo/bokeh';
import { ChevronRightSmall } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Callable } from '@vimeo/core/types/generic';
import { useControlledPopover } from '@vimeo/live-core/hooks/dom/useControlledPopover';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '@vimeo/live-core/hooks/live/useActionConfirmation';
import { openNewFocusedTab } from '@vimeo/live-core/utils/vimeo/redirect';

import { useInteractionToolsPageLink } from '../../../hooks/interaction_tools/useInteractionToolsPageLink';
import { translations } from '../../../translations';

interface IInteractionToolsSubmenuProps extends IBaseDomElementProps {
  placement: string;
  onCloseParentMenu: Callable;
}

export function InteractionToolsSubmenu({
  placement,
  onCloseParentMenu,
}: IInteractionToolsSubmenuProps) {
  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();
  const { isLoading, value, hasCopied, onCopy } = useInteractionToolsPageLink();
  const notify: TNotificationHandler = useSimpleNotification();

  const onCopyLinkClick = useCallback(async () => {
    // todo: tracking?
    onCopy();
  }, [onCopy]);

  const onOpenLinkClick = useCallback(async () => {
    openNewFocusedTab(value);
    onClosePopover();
    onCloseParentMenu();
  }, [onCloseParentMenu, onClosePopover, value]);

  useEffect(() => {
    if (hasCopied) {
      onClosePopover();
      onCloseParentMenu();
      notify({ message: translations.linkCopied });
    }
  }, [hasCopied, notify, onCloseParentMenu, onClosePopover]);

  return (
    <Menu
      isOpen={isOpen}
      placement={placement}
      closeOnSelect={false}
      onOpen={onOpenPopover}
      onClose={onClosePopover}
    >
      <MenuButton
        as={MenuItem}
        ref={triggerRef}
        command={(<ChevronRightSmall boxSize={rem(20)} />) as unknown as string}
        commandSpacing={0}
      >
        {translations.engagementTools}
      </MenuButton>
      <MenuList ref={contentRef as LegacyRef<HTMLDivElement>} width={rem(168)}>
        <MenuItem isDisabled={isLoading} onClick={onCopyLinkClick}>
          {translations.copyLink}
        </MenuItem>
        <MenuItem isDisabled={isLoading} onClick={onOpenLinkClick}>
          {translations.openInNewTab}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
